import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CropModal from "@/components/CropModal";
import { isCroppableImage } from "@/lib/imageProcess";
import {
  Images,
  Upload,
  Printer,
  Loader2,
  FileText,
  Trash2,
  X,
  Save,
  Calendar,
  Camera,
} from "lucide-react";

const COLS = { 4: 2, 6: 2, 9: 3 };

function ReceiptImage({ item, className }) {
  const isPdf = (item.content_type || "").includes("pdf");
  const src = `${API_BASE}${item.image_url.replace("/api", "")}`;
  if (isPdf) {
    return (
      <div className={`flex flex-col items-center justify-center bg-stone-100 text-stone-500 ${className}`}>
        <FileText className="h-8 w-8" />
        <span className="mt-1 text-xs">PDF</span>
      </div>
    );
  }
  return <img src={src} alt={item.memo || "領収書"} loading="lazy" className={`object-cover ${className}`} />;
}

export default function Receipts() {
  const fileRef = useRef();
  const cameraRef = useRef();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [upDate, setUpDate] = useState("");
  const [upMemo, setUpMemo] = useState("");
  const [perPage, setPerPage] = useState(6);
  const [selected, setSelected] = useState(null);
  const [editMemo, setEditMemo] = useState("");
  const [editDate, setEditDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/receipts");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const doUpload = async (fileOrBlob, filename) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fileOrBlob, filename);
      const params = new URLSearchParams();
      if (upDate) params.set("date", upDate);
      if (upMemo) params.set("memo", upMemo);
      await api.post(`/receipts?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("領収書を保存しました");
      setUpDate("");
      setUpMemo("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isCroppableImage(file)) {
      doUpload(file, file.name || "receipt.pdf");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCropApply = async (blob) => {
    setCropSrc(null);
    await doUpload(blob, "receipt.jpg");
  };

  const cancelCrop = () => {
    setCropSrc(null);
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const openDetail = (item) => {
    setSelected(item);
    setEditMemo(item.memo || "");
    setEditDate(item.date || "");
  };

  const saveDetail = async () => {
    if (!selected || selected.source !== "upload") return;
    setSaving(true);
    try {
      await api.put(`/receipts/${selected.id}`, { memo: editMemo, date: editDate });
      toast.success("更新しました");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (item) => {
    if (item.source !== "upload") {
      toast.error("経費の領収書は「経費」画面から削除してください");
      return;
    }
    if (!window.confirm("この領収書を削除しますか？")) return;
    try {
      await api.delete(`/receipts/${item.id}`);
      toast.success("削除しました");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const pages = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < items.length; i += perPage) chunks.push(items.slice(i, i + perPage));
    return chunks;
  }, [items, perPage]);

  const cols = COLS[perPage] || 2;

  return (
    <div data-testid="receipts-page">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .receipt-print-area, .receipt-print-area * { visibility: visible !important; }
          .receipt-print-area { position: absolute; top: 0; left: 0; width: 100%; }
          .no-print { display: none !important; }
          .print-page { page-break-after: always; padding: 12mm; }
          .print-page:last-child { page-break-after: auto; }
          img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="no-print flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-stone-900 flex items-center gap-2">
            <Images className="h-6 w-6 text-emerald-700" />
            レシート帳
          </h1>
          <p className="mt-2 text-sm text-stone-500">
            領収書の写真を保存し、スクラップブック風に印刷できます。経費に添付した領収書も自動で表示されます。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2">
            <span className="text-xs text-stone-500">1ページ</span>
            <select
              data-testid="receipts-perpage"
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="bg-transparent text-sm text-stone-800 focus:outline-none"
            >
              <option value={4}>4枚</option>
              <option value={6}>6枚</option>
              <option value={9}>9枚</option>
            </select>
          </div>
          <button
            onClick={() => window.print()}
            disabled={items.length === 0}
            data-testid="receipts-print-button"
            className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-50"
          >
            <Printer className="h-4 w-4" />
            印刷
          </button>
        </div>
      </div>

      <div className="no-print mt-6 rounded-lg border border-stone-200 bg-white p-6">
        <div className="font-medium text-stone-900">領収書を追加</div>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-sm">日付（任意）</Label>
            <Input type="date" value={upDate} onChange={(e) => setUpDate(e.target.value)} data-testid="receipt-up-date" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm">メモ（任意）</Label>
            <Input value={upMemo} onChange={(e) => setUpMemo(e.target.value)} data-testid="receipt-up-memo" className="mt-1.5" placeholder="例：〇〇商店 消耗品" />
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid="receipt-file-input" />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="receipt-camera-input" />
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => cameraRef.current?.click()}
            disabled={uploading}
            data-testid="receipt-camera-button"
            className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {uploading ? "保存中…" : "撮影"}
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="receipt-upload-button"
            className="inline-flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-60"
          >
            <Upload className="h-4 w-4" />
            写真を選択
          </button>
        </div>
        <p className="mt-2 text-xs text-stone-500">写真は選択後にブラウザ上で切り取りでき、保存時に250dpiへ圧縮されます（PDFはそのまま保存）。</p>
      </div>

      <div className="no-print mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-stone-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-stone-300 bg-white py-16 text-center text-stone-400" data-testid="receipts-empty">
            まだ領収書がありません。上の「撮影」または「写真を選択」から追加してください。
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="receipts-grid">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => openDetail(item)}
                data-testid={`receipt-card-${item.id}`}
                className="group text-left rounded-lg border border-stone-200 bg-white overflow-hidden hover:shadow-md hover:border-emerald-300 transition-all"
              >
                <div className="relative aspect-[4/3] bg-stone-100 overflow-hidden">
                  <ReceiptImage item={item} className="h-full w-full group-hover:scale-105 transition-transform" />
                  <span className={`absolute top-2 right-2 rounded-full px-2 py-0.5 text-[10px] font-medium ${item.source === "expense" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                    {item.source === "expense" ? "経費" : "アップロード"}
                  </span>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 text-xs text-stone-500">
                    <Calendar className="h-3 w-3" />
                    {item.date || "日付なし"}
                  </div>
                  <p className="mt-1 text-sm text-stone-800 line-clamp-2 min-h-[2.5rem]">{item.memo || "（メモなし）"}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="receipt-print-area hidden print:block">
        {pages.map((page, pi) => (
          <div key={pi} className="print-page">
            <div className="mb-4 flex items-center justify-between border-b border-stone-300 pb-2">
              <div className="font-display font-bold text-lg text-stone-900">レシート帳</div>
              <div className="text-xs text-stone-500">ページ {pi + 1} / {pages.length}</div>
            </div>
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
              {page.map((item) => (
                <div key={item.id} className="rounded border border-stone-300 overflow-hidden" style={{ breakInside: "avoid" }}>
                  <div className="bg-stone-50 flex items-center justify-center" style={{ height: perPage === 9 ? "150px" : perPage === 6 ? "210px" : "260px" }}>
                    <ReceiptImage item={item} className="max-h-full max-w-full" />
                  </div>
                  <div className="p-2 border-t border-stone-200">
                    <div className="text-[11px] text-stone-500">{item.date || "日付なし"}</div>
                    <div className="text-xs text-stone-800">{item.memo || "（メモなし）"}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <CropModal src={cropSrc} onCancel={cancelCrop} onApply={handleCropApply} busy={uploading} />

      {selected && (
        <div className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelected(null)} data-testid="receipt-detail-modal">
          <div className="bg-white rounded-lg max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
              <div className="text-sm font-medium text-stone-900">領収書の詳細</div>
              <button onClick={() => setSelected(null)} data-testid="receipt-detail-close" className="text-stone-400 hover:text-stone-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[50vh] overflow-auto bg-stone-100 flex items-center justify-center p-3">
              <ReceiptImage item={selected} className="max-h-[45vh] max-w-full" />
            </div>
            <div className="p-4 space-y-3">
              {selected.source === "upload" ? (
                <>
                  <div>
                    <Label className="text-sm">日付</Label>
                    <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} data-testid="receipt-edit-date" className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-sm">メモ</Label>
                    <Input value={editMemo} onChange={(e) => setEditMemo(e.target.value)} data-testid="receipt-edit-memo" className="mt-1.5" />
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <button onClick={() => removeItem(selected)} data-testid="receipt-delete-button" className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" /> 削除
                    </button>
                    <button onClick={saveDetail} disabled={saving} data-testid="receipt-save-button" className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} 保存
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-sm text-stone-600">
                  <div className="flex items-center gap-1 text-xs text-stone-500"><Calendar className="h-3 w-3" />{selected.date || "日付なし"}</div>
                  <p className="mt-1">{selected.memo || "（メモなし）"}</p>
                  <p className="mt-3 text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">この領収書は「経費」に添付されています。編集・削除は経費画面から行ってください。</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
